/**
 * Device-pairing page (/pair).
 *
 * A desktop or Home Assistant install shows a short code and opens (or
 * asks the parent to open) this page. Once the parent is signed in, we
 * bind that code to their account (`claimDevicePairing`); the device is
 * polling in the background and, on success, receives a one-time custom
 * token it exchanges for a real parent session — so it can sync every
 * profile and run as a worker without the old per-profile "link".
 *
 * If the parent isn't signed in yet we bounce to /auth carrying the code;
 * AuthPage routes back here after SMS login.
 */
import React, {useCallback, useEffect, useState} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  CssBaseline,
  TextField,
  ThemeProvider,
  Typography,
} from '@mui/material';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import {onAuthStateChanged, User} from 'firebase/auth';
import {auth} from '../../firebase';
import {client} from '../../api/graphql';
import {CLAIM_DEVICE_PAIRING} from '../../api/graphql_pair_device';
import {goodShowHQTheme} from '../../theme/planapusTheme';
import BrandChip from '../common/BrandChip';

type Phase = 'checking' | 'entry' | 'working' | 'done' | 'error';

export default function PairDevicePage(): React.JSX.Element {
  const [params] = useSearchParams();
  const codeFromUrl = (params.get('code') ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  const navigate = useNavigate();

  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [code, setCode] = useState(codeFromUrl);
  const [phase, setPhase] = useState<Phase>('checking');
  const [message, setMessage] = useState('');

  useEffect(() => onAuthStateChanged(auth, u => setUser(u)), []);

  // Not signed in → go authenticate, carrying the code so we return here.
  useEffect(() => {
    if (user === null) {
      navigate(`/auth?pair=${encodeURIComponent(codeFromUrl || code)}`, {replace: true});
    } else if (user) {
      setPhase(p => (p === 'checking' ? 'entry' : p));
    }
  }, [user, navigate, codeFromUrl, code]);

  const claim = useCallback(
    async (raw: string) => {
      const clean = raw.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
      if (clean.length < 6) {
        setPhase('error');
        setMessage('Enter the code shown on your device.');
        return;
      }
      setPhase('working');
      try {
        const {data} = await client.mutate<{
          claimDevicePairing: {success: boolean; message: string | null};
        }>({mutation: CLAIM_DEVICE_PAIRING, variables: {code: clean}});
        if (data?.claimDevicePairing.success) {
          setPhase('done');
        } else {
          setPhase('error');
          setMessage(data?.claimDevicePairing.message ?? 'That code didn’t work.');
        }
      } catch {
        setPhase('error');
        setMessage('Something went wrong — please try again.');
      }
    },
    [],
  );

  // Auto-submit when the code came in via the URL and we're signed in.
  useEffect(() => {
    if (user && codeFromUrl && phase === 'entry') {
      void claim(codeFromUrl);
    }
  }, [user, codeFromUrl, phase, claim]);

  return (
    <ThemeProvider theme={goodShowHQTheme}>
      <CssBaseline />
      <Container maxWidth="sm" sx={{py: {xs: 8, md: 12}}}>
        <Box sx={{textAlign: 'center'}}>
          <BrandChip label="Connect a device" />
          {phase === 'done' ? (
            <>
              <CheckCircleRounded sx={{fontSize: 56, color: 'success.main', mt: 3}} />
              <Typography component="h1" sx={{fontSize: 26, fontWeight: 800, mt: 2}}>
                Device connected
              </Typography>
              <Typography sx={{mt: 1.5, color: 'text.secondary'}}>
                Your GoodShowHQ app is signing in and syncing your kids’ profiles now. You can
                close this window.
              </Typography>
            </>
          ) : (
            <>
              <Typography component="h1" sx={{fontSize: 28, fontWeight: 800, mt: 3}}>
                Connect your app
              </Typography>
              <Typography sx={{mt: 1.5, color: 'text.secondary'}}>
                Enter the code shown in your GoodShowHQ desktop or Home Assistant app to link it to
                your account.
              </Typography>

              {phase === 'checking' || phase === 'working' ? (
                <Box sx={{mt: 5, display: 'flex', justifyContent: 'center'}}>
                  <CircularProgress size={28} />
                </Box>
              ) : (
                <Box
                  component="form"
                  onSubmit={(e: React.FormEvent) => {
                    e.preventDefault();
                    void claim(code);
                  }}
                  sx={{mt: 4, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center'}}
                >
                  <TextField
                    value={code}
                    onChange={e => setCode(e.target.value.toUpperCase())}
                    placeholder="ABCD1234"
                    autoFocus
                    inputProps={{
                      style: {
                        textAlign: 'center',
                        letterSpacing: '0.4em',
                        fontSize: 24,
                        fontWeight: 700,
                        fontVariantNumeric: 'tabular-nums',
                      },
                      maxLength: 12,
                      'aria-label': 'Device code',
                    }}
                    sx={{width: 260}}
                  />
                  {phase === 'error' && (
                    <Typography sx={{color: 'error.main', fontSize: 14, fontWeight: 600}}>
                      {message}
                    </Typography>
                  )}
                  <Button type="submit" variant="contained" size="large" sx={{minWidth: 200}}>
                    Connect device
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      </Container>
    </ThemeProvider>
  );
}
